import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  Modal,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { Button } from '@/components/ui/therapist/Button';
import Checkbox from '@/components/ui/therapist/Checkbox';
import { Divider } from 'react-native-elements';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/therapist/Input';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { DifficultyQuery, TemplateQuery } from '@/components/Template/types';
import { Text } from '@/components/Text';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';

import { styles } from './styles';
import colors from '@/config/colors';

const { width } = Dimensions.get('window');

interface TemplateFilterProps {
  isOpen: boolean;
  onClose: () => void;
  drawerWidth?: number;
  difficultyFilter?: DifficultyQuery | undefined;
  setDifficultyFilter: (value: DifficultyQuery | undefined) => void;
  typeFilter?: TemplateQuery | undefined;
  setTypeFilter?: (value: TemplateQuery | undefined) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  resetFilter: () => void;
}

export const TemplateFilter: React.FC<TemplateFilterProps> = ({
  isOpen,
  onClose,
  drawerWidth = width * 0.4,
  difficultyFilter,
  setDifficultyFilter,
  typeFilter,
  setTypeFilter,
  searchTerm,
  setSearchTerm,
  resetFilter,
}) => {
  const [visible, setVisible] = useState<boolean>(isOpen);

  useOverlayGuard(!!visible);
  const translateX = useRef(new Animated.Value(drawerWidth)).current;

  const setDifficultyFilterHelper = (
    value: 'easy,' | 'medium,' | 'hard,' | undefined,
  ) => {
    if (value && difficultyFilter?.includes(value)) {
      const newFilter = difficultyFilter.replace(value, '') as DifficultyQuery;
      setDifficultyFilter(newFilter);
      return;
    }
    if (value && ['easy,', 'medium,', 'hard,'].includes(value)) {
      const newFilter = difficultyFilter ? difficultyFilter + value : value;
      setDifficultyFilter(newFilter as DifficultyQuery);
      return;
    }
  };

  const setTypeFilterHelper = (value: 'Word,' | 'Sentence,' | undefined) => {
    if (value && typeFilter?.includes(value)) {
      const newFilter = typeFilter.replace(value, '') as TemplateQuery;
      setTypeFilter?.(newFilter);
      return;
    }
    if (value && ['Word,', 'Sentence,'].includes(value)) {
      const newFilter = typeFilter ? typeFilter + value : value;
      setTypeFilter?.(newFilter as TemplateQuery);
      return;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      Animated.timing(translateX, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(translateX, {
        toValue: drawerWidth,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        setVisible(false);
      });
    }
  }, [isOpen, drawerWidth, translateX]);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible transparent animationType="none" statusBarTranslucent>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.touchBlocker} />
      </TouchableWithoutFeedback>
      <Animated.View
        style={[
          styles.drawer,
          {
            width: drawerWidth,
            transform: [{ translateX }],
          },
        ]}
      >
        <ScrollView>
          <View style={{ paddingHorizontal: 20 }}>
            <View style={styles.templateFilterHeader}>
              <View
                style={{ gap: 10, flexDirection: 'row', alignItems: 'center' }}
              >
                <Icon name="filter" size={24} />
                <Text
                  style={{
                    fontSize: 18,
                    fontWeight: '600',
                    marginVertical: 10,
                  }}
                >
                  Filter
                </Text>
              </View>
              <Icon name={'close'} size={24} onPress={onClose} />
            </View>
            <View style={{ paddingVertical: 20 }}>
              <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
            </View>
          </View>
          <View style={{ paddingHorizontal: 20 }}>
            <Input
              label="Titel oder Beschreibung"
              value={searchTerm}
              onChange={setSearchTerm}
              iconLeft="magnifyingGlass"
            />
            <View style={{ paddingVertical: 10 }}>
              <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
            </View>
          </View>
          {setTypeFilter ? (
            <View style={{ paddingHorizontal: 20 }}>
              <View>
                <Text style={{ fontSize: 18, marginBottom: 10 }}>
                  Kategorie
                </Text>
              </View>
              <View style={{ gap: 10, marginBottom: 20 }}>
                <View style={styles.checkboxWrapper}>
                  <Checkbox
                    isSelected={typeFilter?.includes('Word,') || false}
                    onPress={() => setTypeFilterHelper('Word,')}
                    label="Wörterset"
                  />
                  <View style={{ paddingRight: 20 }}>
                    <NeolexonIcon name="letter" isInverted={false} size={20} />
                  </View>
                </View>
                <View style={styles.checkboxWrapper}>
                  <Checkbox
                    isSelected={typeFilter?.includes('Sentence,') || false}
                    onPress={() => setTypeFilterHelper('Sentence,')}
                    label="Sätzeset"
                  />
                  <View style={{ paddingRight: 20 }}>
                    <NeolexonIcon
                      name="sentence"
                      isInverted={false}
                      size={20}
                    />
                  </View>
                </View>
              </View>
              <View style={{ paddingVertical: 10 }}>
                <Divider
                  width={1}
                  color={colors.newUI.therapist.primaryBlue10}
                />
              </View>
            </View>
          ) : null}
          <View style={{ paddingHorizontal: 20 }}>
            <View>
              <Text
                style={{ fontSize: 18, fontWeight: '600', marginBottom: 10 }}
              >
                Schwierigkeit
              </Text>
            </View>
            <View style={{ gap: 10, marginBottom: 20 }}>
              <View style={styles.checkboxWrapper}>
                <Checkbox
                  isSelected={difficultyFilter?.includes('easy,') || false}
                  onPress={() => setDifficultyFilterHelper('easy,')}
                  label="Leicht"
                />
                <View style={{ paddingRight: 20 }}>
                  <NeolexonIcon
                    name="weightEasy"
                    isInverted={false}
                    size={20}
                  />
                </View>
              </View>
              <View style={styles.checkboxWrapper}>
                <Checkbox
                  isSelected={difficultyFilter?.includes('medium,') || false}
                  onPress={() => setDifficultyFilterHelper('medium,')}
                  label="Mittel"
                />
                <View style={{ paddingRight: 20 }}>
                  <NeolexonIcon
                    name="weightMedium"
                    isInverted={false}
                    size={20}
                  />
                </View>
              </View>
              <View style={styles.checkboxWrapper}>
                <Checkbox
                  isSelected={difficultyFilter?.includes('hard,') || false}
                  onPress={() => setDifficultyFilterHelper('hard,')}
                  label="Schwer"
                />
                <View style={{ paddingRight: 20 }}>
                  <NeolexonIcon
                    name="weightHard"
                    isInverted={false}
                    size={20}
                  />
                </View>
              </View>
              <View style={{ paddingVertical: 10 }}>
                <Divider
                  width={1}
                  color={colors.newUI.therapist.primaryBlue10}
                />
              </View>
            </View>
            <View style={styles.templateFilterHeader}>
              <Button
                look="primary"
                style={{ alignSelf: 'flex-start' }}
                onPress={() => onClose()}
                label="Filter anwenden "
              />
              <Button
                look="secondary"
                style={{ alignSelf: 'flex-start' }}
                onPress={() => resetFilter()}
                label="Filter zurücksetzen "
              />
            </View>
          </View>
        </ScrollView>
      </Animated.View>
    </Modal>
  );
};
